import { useCallback, useEffect, useRef } from "react";
import type { TourStepInteraction } from "../../types/tours";
import { canAutoPressAnchor, COACH_PRESS_ANCHORS } from "./tourAnchors";
import {
  anchorSelector,
  findRenderedAnchorElement,
  isTourInitiatedPress,
  pressAnchorElement,
  scheduleFrame,
  TOUR_OVERLAY_ATTRIBUTE,
} from "./tourDom";

export interface StepInteractionInput {
  /** Already defaulted and already gated on the step having an anchor to work with. */
  interaction: TourStepInteraction;
  /** The step's anchor id, or null for a centered card. */
  anchorId: string | null;
  /** `${tourKey}:${stepKey}`, unique across the chained tours the overlay stays mounted for. */
  stepKey: string;
  /** Advances the tour. Called at most once per `ClickToContinue` step. */
  onAdvance: () => void;
  /**
   * The tour has just pressed this step's control itself. Records the step against the run — the
   * same record a coach's own click leaves — without advancing, and hands focus back to the bubble.
   */
  onOpened: () => void;
  /**
   * The coach pressed a commit button (`COACH_PRESS_ANCHORS`) while its modal is still open. The
   * step is armed but not advanced yet; the provider uses this moment to note which popups were open.
   */
  onCommitPressed?: () => void;
}

export interface StepInteractionControls {
  /**
   * Stops an armed commit step from advancing on its own. Next calls it before it closes the modal
   * itself, so the modal leaving the page is not read as the coach's save landing.
   */
  disarm: () => void;
}

/**
 * The two interactive step behaviours, both of which work through the real control rather than a
 * simulation of it.
 *
 * `ClickToContinue` watches for the coach activating the anchor and advances the tour. The listener
 * is on `document` in the **capture** phase, so a control whose own handler stops propagation
 * (several popover triggers do) cannot hide the click, and it does not preventDefault or stop
 * anything itself: the control still opens what it opens. A keyboard Enter or Space on a button
 * fires the same click, so no separate key handling is needed, and nothing advances the tour except
 * that one control.
 *
 * `OpenAnchor` clicks the control itself once the step is on screen, so the coach reads the step
 * with the popup already open. It fires **once per step per run**: a second press of a toggle would
 * close it. Back is the one exception, and a deliberate one: it closes what the run opened and
 * re-arms the step it lands on, so the press runs again on a page where the popup is shut. Only the
 * controls in `AUTO_PRESS_ANCHORS` are ever pressed.
 *
 * The "already pressed" record is the run's own `satisfiedStepKeys`, reached through `onOpened`,
 * not a ref in here. One record, four readers: this effect (do not press twice), the overlay (a
 * step with nothing left to press is not interactive, so the page gets its shield back), Back
 * (which popups to put back) and the end of the run (close again what it opened). A ref could only ever be
 * read by the first of those, which is how Back came to
 * land on a spent `OpenAnchor` step — describing a panel that was no longer on screen, over a page
 * left fully live. It also lived for the life of the overlay rather than the run, so an admin
 * preview replacing the same tour inherited it and silently refused the press on a fresh run.
 *
 * A commit button (`COACH_PRESS_ANCHORS`: Attach, Save) is the exception to "the click advances".
 * The coach's press only arms the step; it advances once the commit has finished, which is when the
 * button leaves the page because its modal closed. A save that fails validation, or is still
 * waiting on the server, leaves the button where it is, so the coach stays on the step with the page
 * live and can fix the form. Closing the modal any other way while armed also advances.
 *
 * A control that mounts late needs no watch of its own here. `interaction` is `OpenAnchor` only
 * once the overlay has a measured rect for the anchor, so this effect re-runs the moment the
 * control appears — that gate is fed by `useAnchorState`, which already observes the page for
 * exactly this.
 */
export const useStepInteraction = ({
  interaction,
  anchorId,
  stepKey,
  onAdvance,
  onOpened,
  onCommitPressed,
}: StepInteractionInput): StepInteractionControls => {
  const onAdvanceRef = useRef(onAdvance);
  const onOpenedRef = useRef(onOpened);
  const onCommitPressedRef = useRef(onCommitPressed);
  useEffect(() => {
    onAdvanceRef.current = onAdvance;
    onOpenedRef.current = onOpened;
    onCommitPressedRef.current = onCommitPressed;
  }, [onAdvance, onOpened, onCommitPressed]);
  /** Disarms the current step's armed commit watch, if there is one. */
  const disarmRef = useRef<() => void>(() => {});
  const disarm = useCallback(() => disarmRef.current(), []);

  useEffect(() => {
    if (interaction !== "ClickToContinue" || !anchorId) return;
    const selector = anchorSelector(anchorId);
    const commits = COACH_PRESS_ANCHORS.has(anchorId);
    let advanced = false;
    let observer: MutationObserver | null = null;

    const stopWatching = () => {
      observer?.disconnect();
      observer = null;
    };
    disarmRef.current = () => {
      advanced = true;
      stopWatching();
    };

    // An armed commit step advances once its button has gone: the modal closed.
    const advanceIfCommitted = () => {
      if (advanced || findRenderedAnchorElement(anchorId)) return;
      advanced = true;
      stopWatching();
      onAdvanceRef.current();
    };

    const arm = () => {
      onCommitPressedRef.current?.();
      if (observer || typeof MutationObserver === "undefined") return;
      observer = new MutationObserver(advanceIfCommitted);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "inert", "aria-hidden", "hidden", "style"],
      });
    };

    const handleClick = (event: MouseEvent) => {
      if (advanced || !(event.target instanceof Element)) return;
      // A press the tour made itself is not the coach doing as the step asked. The end-of-run
      // tidy-up closes what the run opened, and an admin can author a tour whose last step watches
      // the very anchor the tidy-up presses — which would record the run a second time, with
      // whatever outcome the advance produced.
      if (isTourInitiatedPress()) return;
      const hit = event.target.closest(selector);
      // The overlay carries no anchors of its own, but a tour running over the admin pick-mode
      // layer or a future in-bubble anchor must not count as the coach pressing the control.
      if (!hit || hit.closest(`[${TOUR_OVERLAY_ATTRIBUTE}]`)) return;
      if (commits) {
        arm();
        return;
      }
      // Once only: a double click would otherwise skip the step after this one.
      advanced = true;
      onAdvanceRef.current();
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      stopWatching();
      advanced = true;
      disarmRef.current = () => {};
    };
  }, [interaction, anchorId, stepKey]);

  useEffect(() => {
    if (interaction !== "OpenAnchor" || !anchorId) return;
    if (!canAutoPressAnchor(anchorId)) return;

    // A frame late, so the step's own render (and anything the previous step's click opened) has
    // landed before the page is touched.
    return scheduleFrame(() => {
      const element = findRenderedAnchorElement(anchorId);
      // Gone again between the gate and this frame. The gate re-runs this effect when it comes
      // back, and nothing is recorded, so the press is not lost.
      if (!element) return;
      // A trigger that says it is already open would be closed by a press, not opened: the coach
      // got there first, or an earlier step asked them to. The overlay reads the same flag and has
      // already made the step read-and-Next, so this is the belt to that braces. A trigger with no
      // `aria-expanded` says nothing either way, so the press goes ahead.
      if (element.getAttribute("aria-expanded") === "true") return;
      pressAnchorElement(element);
      // Recorded only for a press that actually landed: that is what makes this step read-and-Next
      // from here on, and what the end of the run reads to close again what it opened.
      onOpenedRef.current();
    });
  }, [interaction, anchorId, stepKey]);

  return { disarm };
};
